import React, { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import GameBoard from './GameBoard';
import './OnlineGameBoard.css';

const OnlineGameBoard = ({ matchData, onGameEnd }) => {
  const { socket, makeMove, sendGameOver, resetMatch } = useSocket();
  const { user } = useAuth();
  const [opponentMove, setOpponentMove] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [opponentDisconnected, setOpponentDisconnected] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);

  const isWhite = matchData.whitePlayer.username === user.username;
  const myColor = isWhite ? 'white' : 'black';
  const opponentColor = isWhite ? 'black' : 'white';
  const opponentName = isWhite 
    ? matchData.blackPlayer.username 
    : matchData.whitePlayer.username;
  const opponentRating = isWhite
    ? matchData.blackPlayer.rating
    : matchData.whitePlayer.rating;

  useEffect(() => {
    if (!socket) return;

    // Listen for opponent's moves
    const handleMoveMade = (data) => {
      if (data.playerId !== user.id) {
        console.log('Opponent made move:', data.move);
        setOpponentMove(data.move);
      }
    };

    // Listen for game end
    const handleGameEnded = (data) => {
      console.log('Game ended:', data);
      setGameEnded(true);
      
      setTimeout(() => {
        resetMatch();
        onGameEnd(data);
      }, 3000);
    };

    // Listen for opponent disconnect
    const handlePlayerDisconnected = (data) => {
      if (data.username === opponentName) {
        console.log('Opponent disconnected');
        setOpponentDisconnected(true);
      }
    };

    // Listen for chat messages
    const handleChatMessage = (data) => {
      setChatMessages(prev => [...prev, data]);
    };

    socket.on('move_made', handleMoveMade);
    socket.on('game_ended', handleGameEnded);
    socket.on('player_disconnected', handlePlayerDisconnected);
    socket.on('chat_message', handleChatMessage);

    return () => {
      socket.off('move_made', handleMoveMade);
      socket.off('game_ended', handleGameEnded);
      socket.off('player_disconnected', handlePlayerDisconnected);
      socket.off('chat_message', handleChatMessage);
    };
  }, [socket, user.id, user.username, opponentName, resetMatch, onGameEnd]);

  const handleMove = useCallback((moveData) => {
    // Send move to server
    makeMove(matchData.gameId, moveData);
  }, [matchData.gameId, makeMove]);

  const handleGameOver = useCallback((winner, reason) => {
    if (!gameEnded) {
      console.log('Game over:', winner, reason);
      sendGameOver(matchData.gameId, winner, reason);
      setGameEnded(true);
    }
  }, [matchData.gameId, sendGameOver, gameEnded]);

  return (
    <div className="online-game-container">
      <div className="online-game-header">
        <div className="game-info">
          <h2>🎮 Online Match</h2>
          <div className="players-info">
            <div className={`player-card ${isWhite ? 'my-card' : ''}`}>
              <span className="player-name">
                {matchData.whitePlayer.username}
                {isWhite && ' (You)'}
              </span>
              <span className="player-rating">
                ⭐ {matchData.whitePlayer.rating}
              </span>
              <span className="player-color">⚪ White</span>
            </div>
            <div className="vs-divider">VS</div>
            <div className={`player-card ${!isWhite ? 'my-card' : ''}`}>
              <span className="player-name">
                {matchData.blackPlayer.username}
                {!isWhite && ' (You)'}
              </span>
              <span className="player-rating">
                ⭐ {matchData.blackPlayer.rating}
              </span>
              <span className="player-color">⚫ Black</span>
            </div>
          </div>
        </div>
      </div>

      {opponentDisconnected && !gameEnded && (
        <div className="disconnect-warning">
          ⚠️ {opponentName} has disconnected. Waiting for reconnection...
        </div>
      )}

      <GameBoard 
        mode="online"
        gameData={{
          id: matchData.gameId,
          whitePlayerName: matchData.whitePlayer.username,
          blackPlayerName: matchData.blackPlayer.username,
          gameMode: 'online',
          playerColor: myColor
        }}
        onMove={handleMove}
        opponentMove={opponentMove}
        onGameOver={handleGameOver}
        onBackToMenu={() => {
          if (!gameEnded && window.confirm('Are you sure you want to forfeit?')) {
            handleGameOver(opponentColor, 'forfeit');
          }
        }}
      />

      {chatMessages.length > 0 && (
        <div className="chat-panel">
          <h3>Chat</h3>
          <div className="chat-messages">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className="chat-message">
                <strong>{msg.username}:</strong> {msg.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineGameBoard;
